// Pilot (NetSuite CPQ product code PILOT_CPQ, internal id 7).
//
// Shares nothing behavioural with the other configurators. Empty sets and false flags
// below are DECLARATIONS, not omissions.
//
// Its export sets item fields DIRECTLY -- the single ITCR carries 33 custitem_* fields
// and NO custitem_mapping.
//
// NOT YET LAUNCHABLE (registry enabled:false). It is also
// custrecord_cpqc_product_disabled: true in NetSuite itself, so it is not live there
// either. Two blockers, both verified by running the real pipeline:
//
//   1. NO ITCR CAN MATCH. Its only item-creation rule is SOLE/CFGSTATUS[="FINAL"],
//      parsed to (__["SOLE/CFGSTATUS"]=="FINAL"). SOLE is a CPQ SYSTEM variable -- the
//      configuration's status -- and there is no question behind it anywhere in the
//      export, so the accessor returns undefined and the rule is permanently false. 0
//      ITCRs match, so no item is ever built. No other product references CFGSTATUS.
//      The semantics line up with cpq2's Save and Submit (that IS the final moment), so
//      the fix is to supply that system value when building -- but it changes rule
//      evaluation for everyone, so it is not being slipped in unannounced.
//
//   2. A HELPER CROSSES OPERATION SCOPES. cleanNozzleLabel is DEFINED in the
//      "ID/display/key/description pilot" operation and CALLED by the
//      "ID/display/key/description full assembly" operation. Those two have mutually
//      exclusive trigger rules (ASSEMBLY_PILOT vs ASSEMBLY_FULL) so they never run
//      together, which means NetSuite must evaluate all operation sources in ONE shared
//      scope and hoist their function declarations. cpq2 wraps each operation in its own
//      IIFE, so for a FULL assembly the call is a ReferenceError: 1 of 5 operations
//      fails and the ID Key / Display Name / Description for that branch are not built.
//      Fixing it means changing how operations are scoped, which affects Natural Draft's
//      55 operations too, so it needs a deliberate decision rather than a quiet change.
import type { ProductProfile } from "./profile";

export const PILOT_PROFILE: ProductProfile = {
  slug: "pilot",

  // Nothing manually hidden. FINS_CAP is also a Natural Draft question code, and
  // DESCRIPTION / DISPLAY_NAME / ID_KEY / SALES_DESCRIPTION are shared with other
  // configurators -- more collisions that a product-blind set of codes would misapply.
  hiddenQuestionCodes: new Set(),

  // Its one tab (MAIN) has four groups, all populated, so there is no empty group to
  // suppress. MAIN is shared with Flame Cells and Flange, which is why this map is
  // keyed by product rather than by tab code alone.
  hiddenGroupCodesByTab: {},

  // No option is narrowed beyond what this product's export already says.
  answerHideRules: [],

  optionalQuestionCodes: new Set(),
  optionalQuestionPairs: [],
  hardRequiredQuestionCodes: new Set(),
  suffixNormalizedFields: new Set(),

  // No FAH concept: that is a Natural Draft burner configuration.
  fahSectionGroupCodes: new Set(),
  // No typeable dropdown here: every list this CPQ shows is short enough to pick from.
  comboBoxQuestionCodes: new Set(),
  // Nothing here may invent a NetSuite list value.
  createsAnswersFor: new Set(),
  // Nothing is auto-created here, and no question is restricted to whole numbers,
  // because this product has no typeable list at all.
  autoCreatesAnswersFor: new Set(),
  wholeNumberQuestionCodes: new Set(),
  unlistedSizeTextFallback: new Map(),
  maxInches: new Map(),

  // No encoded mapping, so nothing to exempt from mapping-mode nulling.
  mappingHideExemptCodes: new Set(),

  // Matches its ITCR's {ID_KEY/ID}. Note this is NOT Flange's ID_KEY/ID_KEY: both
  // products have a question called ID_KEY and its ANSWER code differs, so even the
  // fingerprint cannot be shared between them.
  fingerprintAnswerKey: "ID_KEY/ID",

  submit: {
    // A pilot assembly is not a Natural Draft burner housing: no serial tag, no NDB
    // add-on lines, no NDB price list, no NDB customer-requirements record, and none of
    // the custitem_* writes keyed by Natural Draft answer codes.
    serialTag: false,
    additionalLines: false,
    listPricing: false,
    customerRequirements: false,
    ndbItemFields: false,
  },

  // Writes no sales-order body fields.
  orderFields: [],
};
